import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCustomerSchema,
  type CreateCustomerInput,
  type Customer,
} from '@anchor-comms/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function CustomersPage() {
  const [q, setQ] = useState('');
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['customers', q],
    queryFn: () => api<{ items: Customer[] }>('/api/v1/customers', { query: { q } }),
  });

  const create = useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      api<Customer>('/api/v1/customers', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      form.reset();
    },
  });

  const form = useForm<CreateCustomerInput>({
    resolver: zodResolver(CreateCustomerSchema),
    defaultValues: {
      name: '',
      email: undefined,
      phone: undefined,
      company: undefined,
      bookingReference: undefined,
      notes: '',
    },
  });

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <div className="md:col-span-2 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">Customers</h1>
          <Input
            placeholder="Search by name, email, company, booking ref"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <div className="space-y-2">
          {list.data?.items.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {c.company ?? ''}
                    {c.email && ` · ${c.email}`}
                    {c.phone && ` · ${c.phone}`}
                  </div>
                </div>
                {c.bookingReference && (
                  <span className="text-xs text-muted-foreground self-start">Booking {c.bookingReference}</span>
                )}
              </div>
              {c.notes && <p className="text-sm mt-2 text-muted-foreground">{c.notes}</p>}
            </Card>
          ))}
          {list.data && list.data.items.length === 0 && (
            <p className="text-muted-foreground">No customers found.</p>
          )}
        </div>
      </div>

      <Card className="self-start">
        <CardHeader>
          <CardTitle>Add customer</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit((v) =>
              create.mutate({
                name: v.name,
                email: v.email || undefined,
                phone: v.phone || undefined,
                company: v.company || undefined,
                bookingReference: v.bookingReference || undefined,
                notes: v.notes ?? '',
              }),
            )}
          >
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register('name')} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...form.register('email')} />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" {...form.register('phone')} />
            </div>
            <div>
              <Label htmlFor="company">Company</Label>
              <Input id="company" {...form.register('company')} />
            </div>
            <div>
              <Label htmlFor="bookingReference">Booking reference</Label>
              <Input id="bookingReference" {...form.register('bookingReference')} />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} {...form.register('notes')} />
            </div>
            <Button type="submit" disabled={create.isPending} className="w-full">
              {create.isPending ? 'Adding…' : 'Add customer'}
            </Button>
            {create.error && (
              <p className="text-sm text-destructive">{(create.error as Error).message}</p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
